import { useContext, useState, createContext, useEffect } from "react";
import { auth, firestoreDB } from "../firebase";
import { 
  createUserWithEmailAndPassword, 
  onAuthStateChanged, 
  signInWithEmailAndPassword,
  signOut,
  updatePassword,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  getIdToken,
  updateProfile,
  reauthenticateWithPopup,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from "firebase/auth";
import { getDoc, doc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

const AuthContext = createContext();

export function useAuth() {
    return useContext(AuthContext);
}

// match /accounts/{uid} {allow read, write: if uid == request.auth.uid;}
export function AuthProvider({ children }) {

    const url = "https://cluster.pageparrot.com";
    // const url = "https://slackapp.ngrok.dev";
    // TODO: Change this to the correct URL once it's properly setup
    const teamsUrl = "https://pageparrotffc398.azurewebsites.net";

    // User State, takes care of rerendering when user changes
    const [currentUser, setCurrentUser] = useState(null);
    // State, dont render until we have checked if user is logged in or not from local storage
    const [loading, setLoading] = useState(true);

    // Signup function, perform the firebase auth signup
    async function signup(email, password) {
        // removed the send verification code as it made the loading too long for a repeatable action
        // will navigate to verify and finish the send verification code all from the signup success
        return createUserWithEmailAndPassword(auth, email, password);
    }

    // Signup function, perform the firebase auth signin with email and password
    function login(email, password) {
        return signInWithEmailAndPassword(auth, email, password);
    }

    async function sendVerificationCode() {
        const functions = getFunctions();
        const sendCode = httpsCallable(functions, 'sendVerificationCode');
        try {
            const result = await sendCode();
            return result.data;
        } catch (error) {
            console.error("Error sending verification code:", error);
        }
    }

    async function sendPasswordResetEmail(email) {
        const functions = getFunctions();
        const sendPasswordReset = httpsCallable(functions, 'sendPasswordResetEmail');
        return sendPasswordReset({ email });
    }

    async function sendReverificationCode(email) {
        const functions = getFunctions();
        const sendReverificationCode = httpsCallable(functions, 'sendReverificationCode');
        return sendReverificationCode({ email });
    }

    async function verifyReverificationCode(code, isInboxEmailVerification = false) {
        const functions = getFunctions();
        const verifyReverificationCode = httpsCallable(functions, 'verifyReverificationCode');
        return verifyReverificationCode({ code, isInboxEmailVerification });
    }

    async function getPasswordResetRequestEmail(id) {
        const docRef = doc(firestoreDB, "Mail", id);
        const docSnap = await getDoc(docRef);
        if (!docSnap.exists()) {
            return null;
        }
        const data = docSnap.data();
        return data?.template?.data?.userEmail || null;
    }

    async function verifyCode(code) {
        const functions = getFunctions();
        const verify = httpsCallable(functions, 'verifyCode');
        return verify({ code });
    }

    // guess
    function logout() {
        return signOut(auth);
    }

    // i changed it nerd eat it 
    async function resetPassword(id, password) {
        const functions = getFunctions();
        const resetPassword = httpsCallable(functions, 'resetPassword');
        try {
            const result = await resetPassword({ id, password });
            return result.data;
        } catch (error) {
            console.error("Error resetting password:", error);
            throw error;
        }
    }
    
    // does
    function updateCurrentUserPassword(password) {
        return updatePassword(currentUser, password);
    }

    // champ
    function updateCurrentUserDisplayName(displayName) {
        return updateProfile(currentUser, { displayName });
    }

    function getAuthProvider() {
        return currentUser.providerData[0].providerId;
    }

    function lastLoginWithin(minutes) {
        return currentUser.metadata.lastLoginAt > Date.now() - minutes * 60 * 1000;
    }

    async function reauth(provider, password) {
        // Reauth based on provider
        switch(provider) {
            case 'google.com':
                const googleProvider = new GoogleAuthProvider();
                return reauthenticateWithPopup(currentUser, googleProvider);
            case 'microsoft.com': 
                const msProvider = new OAuthProvider('microsoft.com');
                return reauthenticateWithPopup(currentUser, msProvider);
            case 'password':
                const credentials = EmailAuthProvider.credential(currentUser.email, password);
                return reauthenticateWithCredential(currentUser, credentials);
            default:
                throw new Error('Unknown auth provider');
        }
    }

    async function refreshLogin() {
        await currentUser.reload();
        setCurrentUser(auth.currentUser);
        console.log("Current user: ", currentUser);
    }

    function googleLogin() {
        const provider = new GoogleAuthProvider();
        provider.setCustomParameters({ prompt: 'select_account' });
        return signInWithPopup(auth, provider);
    }

    function microsoftLogin() {
        const provider = new OAuthProvider('microsoft.com');
        provider.setCustomParameters({
            // Force re-consent.
            prompt: 'consent',
            // Target specific email with login hint.
            login_hint: 'user@firstadd.onmicrosoft.com'
        });
        provider.addScope('email');
        return signInWithPopup(auth, provider);
        //signInWithRedirect(auth, provider);
        //return getRedirectResult(auth);
    }

    function getAuthToken(force = false) { // Don't default to true, it's not needed
        return getIdToken(currentUser, force);
    }

    // useEffect hook, runs only once on component mount
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setCurrentUser(user);
            setLoading(false);
        });

        return unsubscribe; // when component unmounts, unsubscribe from the listener
    }, []);

    const contextValues = {
        currentUser,
        url,
        teamsUrl,
        signup,
        login,
        logout,
        resetPassword,
        updateCurrentUserPassword,
        googleLogin,
        microsoftLogin,
        getAuthToken,
        verifyCode,
        sendVerificationCode,
        refreshLogin,
        sendPasswordResetEmail,
        getPasswordResetRequestEmail,
        updateCurrentUserDisplayName,
        reauth,
        getAuthProvider,
        lastLoginWithin,
        sendReverificationCode,
        verifyReverificationCode,
    }

    return (
        <AuthContext.Provider value={contextValues}>
            {!loading && children}
        </AuthContext.Provider>
    );
}